export const normalizeUrl = url => {
  const [path, query = ""] = String(url || "").split("?");
  const normalizedPath = path.replace(/^\/+|\/+$/g, "");
  return {
    path: normalizedPath,
    queryParams: new URLSearchParams(query),
  };
};

export const getPage = queryParams => {
  const page = parseInt(queryParams.get("page") || "1", 10);
  return Number.isNaN(page) || page < 1 ? 1 : page;
};

export const getPageSize = queryParams => {
  const pageSize = parseInt(queryParams.get("page_size") || "10", 10);
  return Number.isNaN(pageSize) || pageSize < 1 ? 10 : pageSize;
};

const collectSearchableText = (value, parts = []) => {
  if (value == null) {
    return parts;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    parts.push(String(value));
    return parts;
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectSearchableText(item, parts));
    return parts;
  }
  if (typeof value === "object") {
    Object.values(value).forEach(item => collectSearchableText(item, parts));
  }
  return parts;
};

export const getRowSubUserId = row => {
  if (!row || typeof row !== "object") {
    return null;
  }
  const val = row.sub_user ?? row.sub_user_id;
  if (val == null || val === "") {
    return null;
  }
  return String(val);
};

export const parseSubUserIdFromPath = segments => {
  if (
    segments.length >= 3 &&
    segments[1] === "sub-user" &&
    /^\d+$/.test(segments[2])
  ) {
    return segments[2];
  }
  return null;
};

export const filterRowsBySubUserScope = (rows, queryParams, subUserIdFromPath = null) => {
  const parentUserOnly = queryParams.get("parent_user_only") === "true";
  const subusersOnly = queryParams.get("subusers_only") === "true";

  return (rows || []).filter(row => {
    const rowSubUser = getRowSubUserId(row);

    if (subUserIdFromPath) {
      return rowSubUser === String(subUserIdFromPath);
    }
    if (parentUserOnly) {
      return rowSubUser == null;
    }
    if (subusersOnly) {
      return rowSubUser != null;
    }
    return true;
  });
};

export const filterRows = (rows, queryParams) => {
  const search = (queryParams.get("search") || queryParams.get("query") || "").trim().toLowerCase();

  if (!search) {
    return rows;
  }

  return rows.filter(row =>
    collectSearchableText(row).some(text => text.toLowerCase().includes(search))
  );
};

export const filterRowsByStatus = (rows, queryParams) => {
  const status = queryParams.get("status");
  if (!status) {
    return rows;
  }

  return (rows || []).filter(row => String(row.status) === String(status));
};

const normalizeBoolQuery = value => {
  if (value === "true" || value === "1") {
    return true;
  }
  if (value === "false" || value === "0") {
    return false;
  }
  return null;
};

/** Filter by exact boolean query params used by sub-users list (is_active, distribution, is_blacklisted). */
export const filterRowsByBooleanFlags = (rows, queryParams) => {
  const flags = ["is_active", "distribution", "is_blacklisted"];
  let filtered = rows || [];

  flags.forEach(field => {
    const expected = normalizeBoolQuery(queryParams.get(field));
    if (expected == null) {
      return;
    }
    filtered = filtered.filter(row => {
      const raw = row?.[field];
      const actual = raw === true || raw === "true" || raw === 1 || raw === "1";
      return actual === expected;
    });
  });

  return filtered;
};

const parseSortableValue = (field, value) => {
  if (value == null || value === "") {
    return null;
  }

  if (field === "issue_date" && typeof value === "string") {
    const parts = value.split(".");
    if (parts.length === 3) {
      const [day, month, year] = parts.map(part => Number(part));
      if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
        return new Date(year, month - 1, day).getTime();
      }
    }
  }

  return value;
};

export const sortRows = (rows, queryParams) => {
  const ordering = queryParams.get("ordering");

  if (!ordering) {
    return rows;
  }

  const descending = ordering.startsWith("-");
  const field = descending ? ordering.slice(1) : ordering;

  return [...rows].sort((a, b) => {
    const left = parseSortableValue(field, a[field]);
    const right = parseSortableValue(field, b[field]);

    if (left === right) return 0;
    if (left === undefined || left === null) return 1;
    if (right === undefined || right === null) return -1;

    if (typeof left === "number" && typeof right === "number") {
      return (left - right) * (descending ? -1 : 1);
    }

    return String(left).localeCompare(String(right), undefined, { numeric: true }) * (descending ? -1 : 1);
  });
};

export const paginateRows = (rows, queryParams, options = {}) => {
  const { subUserIdFromPath = null } = options;
  const scopedRows = filterRowsBySubUserScope(rows, queryParams, subUserIdFromPath);
  const statusFilteredRows = filterRowsByStatus(scopedRows, queryParams);
  const flagFilteredRows = filterRowsByBooleanFlags(statusFilteredRows, queryParams);
  const filteredRows = sortRows(filterRows(flagFilteredRows, queryParams), queryParams);
  const page = getPage(queryParams);
  const pageSize = getPageSize(queryParams);
  const start = (page - 1) * pageSize;

  return {
    count: filteredRows.length,
    results: filteredRows.slice(start, start + pageSize),
  };
};

export const getDetail = (rows, id) => {
  const match = rows.find(row => String(row.id) === String(id));
  return match || rows[0] || {};
};
